import React from 'react';
import { Link } from 'react-router-dom';
import useReveal from '../Utils/useReveal';
import styles from './Timeline.module.css';

const monogram = (org) => {
  const words = org.split(/\s+/).filter(Boolean);
  // Two words -> initials ("Roots 2" -> R2); one word -> first two letters ("DeepTrekker" -> DT)
  if (words.length > 1) return words.slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  // Single word: prefer internal capitals ("DeepTrekker" -> DT), else first two letters
  const caps = org.match(/[A-Z]/g) || [];
  const raw = caps.length > 1 ? caps.slice(0, 2).join('') : org.slice(0, 2);
  return raw.toUpperCase();
};

/**
 * One node on the vertical timeline. `active` is driven by useActiveIndex so
 * the node nearest the viewport centre lights up as you scroll.
 */
export default function TimelineItem({ item, active, innerRef }) {
  const [revealRef, revealed] = useReveal();
  const isExternal = Boolean(item.external);

  const body = (
    <>
      <div className={styles.head}>
        <span className={styles.date}>{item.dateLabel}</span>
        {item.ongoing && <span className={styles.live}>Current</span>}
      </div>

      <h3 className={styles.org}>
        {item.logo ? (
          <img className={styles.logo} src={item.logo} alt="" loading="lazy" decoding="async" />
        ) : (
          <span className={styles.monogram} aria-hidden="true">{monogram(item.org)}</span>
        )}
        <span>{item.org}</span>
      </h3>

      <p className={styles.role}>{item.role}</p>
      <p className={styles.summary}>{item.summary}</p>

      {item.metrics?.length > 0 && (
        <ul className={styles.metrics}>
          {item.metrics.map((m) => (
            <li key={m.label} className="mc-metric">
              <b>{m.value}</b>
              <span>{m.label}</span>
            </li>
          ))}
        </ul>
      )}

      {item.stack?.length > 0 && (
        <ul className={styles.stack}>
          {item.stack.map((s) => (
            <li key={s} className="mc-chip">{s}</li>
          ))}
        </ul>
      )}

      <span className={styles.cta}>
        {isExternal ? 'View on GitHub' : 'View writeup'}
        <span className={styles.arrow} aria-hidden="true">→</span>
      </span>
    </>
  );

  return (
    <li
      ref={innerRef}
      className={styles.item}
      data-active={active}
      data-repeat={Boolean(item.sameOrgAsPrevious)}
    >
      <span className={styles.node} aria-hidden="true" />

      <div ref={revealRef} className={`${styles.card} mc-reveal`} data-revealed={revealed}>
        {isExternal ? (
          <a className={styles.link} href={item.route} target="_blank" rel="noreferrer">
            {body}
          </a>
        ) : (
          <Link className={styles.link} to={item.route}>{body}</Link>
        )}
      </div>
    </li>
  );
}
